import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Server, Users, Globe, Settings, Trash2, Edit, Eye, UserPlus, Upload } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { edgeTunnelApi } from '@/lib/api'
import { toast } from 'react-hot-toast'
import EdgeTunnelGroupFormEnhanced from '@/components/admin/EdgeTunnelGroupFormEnhanced'
import NodeEditModal from '@/components/admin/NodeEditModal'
import OverrideImportModal from '@/components/admin/OverrideImportModal'

export default function EdgeTunnel() {
  const [activeTab, setActiveTab] = useState('groups')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingGroup, setEditingGroup] = useState<any>(null)
  const queryClient = useQueryClient()

  // 获取EdgeTunnel服务组列表
  const { data: groupsData, isLoading: groupsLoading } = useQuery({
    queryKey: ['edgetunnel-groups'],
    queryFn: () => edgeTunnelApi.getGroups(),
  })

  // 获取节点列表 - 始终启用以获取统计数据
  const { data: nodesData, isLoading: nodesLoading, error: nodesError } = useQuery({
    queryKey: ['edgetunnel-nodes'],
    queryFn: () => edgeTunnelApi.getNodes({ limit: 1000 }) // 增加每页显示数量到1000
  })

  // 获取用户分配列表
  const { data: assignmentsData, isLoading: assignmentsLoading, error: assignmentsError } = useQuery({
    queryKey: ['edgetunnel-assignments-v2'], // 更改key强制重新获取
    queryFn: () => edgeTunnelApi.getUserAssignments()
  })

  // 删除服务组
  const deleteMutation = useMutation({
    mutationFn: (id: string) => edgeTunnelApi.deleteGroup(id),
    onSuccess: () => {
      toast.success('服务组删除成功')
      queryClient.invalidateQueries({ queryKey: ['edgetunnel-groups'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '删除失败')
    },
  })

  // 切换服务组状态
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: number }) => 
      edgeTunnelApi.updateGroup(id, { status }),
    onSuccess: () => {
      toast.success('服务组状态更新成功')
      queryClient.invalidateQueries({ queryKey: ['edgetunnel-groups'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '状态更新失败')
    },
  })

  const handleDelete = (group: any) => {
    if (group.user_count > 0) {
      toast.error('该服务组还有用户在使用，无法删除')
      return
    }
    
    if (confirm(`确定要删除服务组"${group.name}"吗？`)) {
      deleteMutation.mutate(group.id)
    }
  }

  const handleEdit = (group: any) => {
    setEditingGroup(group)
    setShowCreateForm(true)
  }

  const handleToggleStatus = (group: any) => {
    const newStatus = group.status === 1 ? 0 : 1
    const statusText = newStatus === 1 ? '启用' : '禁用'
    
    if (confirm(`确定要${statusText}服务组"${group.name}"吗？`)) {
      toggleStatusMutation.mutate({ id: group.id, status: newStatus })
    }
  }

  const handleFormSuccess = () => {
    setShowCreateForm(false)
    setEditingGroup(null)
    queryClient.invalidateQueries({ queryKey: ['edgetunnel-groups'] })
  }

  const groups = groupsData?.data?.data?.groups || []
  // 修复节点数据路径 - 可能也是4层嵌套
  const nodes = nodesData?.data?.data?.data?.nodes || 
                nodesData?.data?.data?.nodes || 
                nodesData?.data?.nodes || 
                []
  
  // 修复数据路径 - API返回的是4层嵌套结构
  let assignments = []
  if (assignmentsData) {
    assignments = assignmentsData?.data?.data?.data?.assignments || 
                  assignmentsData?.data?.data?.assignments || 
                  assignmentsData?.data?.assignments || 
                  assignmentsData?.assignments || 
                  []
  }
  
  // 数据统计
  console.log('EdgeTunnel数据统计 - 节点:', nodes?.length, '用户分配:', assignments?.length)

  // 标签页配置
  const tabs = [
    { id: 'groups', name: '服务组管理', icon: Server, count: groups.length },
    { id: 'nodes', name: '节点管理', icon: Globe, count: nodes.length },
    { id: 'assignments', name: '用户分配', icon: Users, count: assignments.length }
  ]

  const isLoading = 
    (activeTab === 'groups' && groupsLoading) ||
    (activeTab === 'nodes' && nodesLoading) ||
    (activeTab === 'assignments' && assignmentsLoading)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面头部 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">EdgeTunnel管理</h1>
          <p className="text-gray-600 mt-1">管理EdgeTunnel服务组、节点和用户分配</p>
        </div>
        <Button 
          onClick={() => {
            setEditingGroup(null)
            setShowCreateForm(true)
          }}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          添加服务组
        </Button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Server className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">服务组总数</p>
                <p className="text-2xl font-bold text-gray-900">{groups.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Globe className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">节点总数</p>
                <p className="text-2xl font-bold text-gray-900">{nodes.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-purple-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">用户总数</p>
                <p className="text-2xl font-bold text-gray-900">{assignments.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Settings className="h-8 w-8 text-orange-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">活跃服务</p>
                <p className="text-2xl font-bold text-gray-900">
                  {groups.filter((group: any) => group.status === 1).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 标签页导航 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.name}
                <Badge variant="secondary" className="ml-1">
                  {tab.count}
                </Badge>
              </button>
            )
          })}
        </nav>
      </div>

      {/* 标签页内容 */}
      <div className="mt-6">
        {activeTab === 'groups' && <GroupsTab groups={groups} onEdit={handleEdit} onDelete={handleDelete} onToggleStatus={handleToggleStatus} />}
        {activeTab === 'nodes' && <NodesTab nodes={nodes} groups={groups} />}
        {activeTab === 'assignments' && <AssignmentsTab assignments={assignments} />}
      </div>

      {/* 创建/编辑表单 */}
      {showCreateForm && (
        <EdgeTunnelGroupFormEnhanced
          group={editingGroup}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowCreateForm(false)
            setEditingGroup(null)
          }}
        />
      )}
    </div>
  )
}

// 服务组标签页组件
function GroupsTab({ groups, onEdit, onDelete, onToggleStatus }: any) {
  const [showOverrideModal, setShowOverrideModal] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<any>(null)
  const queryClient = useQueryClient()

  const handleOverrideImport = (group: any) => {
    setSelectedGroup(group)
    setShowOverrideModal(true)
  }
  if (groups.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Server className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">暂无EdgeTunnel服务组</h3>
          <p className="text-gray-500 mb-4">开始创建您的第一个EdgeTunnel服务组</p>
          <Button className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            添加服务组
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {groups.map((group: any) => (
        <Card key={group.id} className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Server className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">{group.name}</CardTitle>
                  <p className="text-sm text-gray-500">{group.description || '暂无描述'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge 
                  variant={group.status === 1 ? "default" : "secondary"}
                  className="cursor-pointer hover:opacity-80"
                  onClick={() => onToggleStatus(group)}
                >
                  {group.status === 1 ? '启用' : '禁用'}
                </Badge>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(group)}
                    title="编辑服务组"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onToggleStatus(group)}
                    title={group.status === 1 ? '禁用服务组' : '启用服务组'}
                    className={group.status === 1 ? 'text-orange-500 hover:text-orange-600' : 'text-green-500 hover:text-green-600'}
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOverrideImport(group)}
                    title="覆盖导入节点"
                    className="text-blue-500 hover:text-blue-600"
                  >
                    <Upload className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(group)}
                    disabled={false}
                    title="删除服务组"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="pt-0">
            <div className="space-y-3">
              {/* API信息 */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">API地址:</span>
                  <span className="font-mono text-xs bg-white px-2 py-1 rounded border">
                    {group.api_endpoint}
                  </span>
                </div>
              </div>

              {/* 统计信息 */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <div className="text-lg font-bold text-blue-600">{group.node_count || 0}</div>
                  <div className="text-xs text-gray-500">节点数</div>
                </div>
                <div className="p-2 bg-green-50 rounded-lg">
                  <div className="text-lg font-bold text-green-600">{group.user_count || 0}</div>
                  <div className="text-xs text-gray-500">用户数</div>
                </div>
                <div className="p-2 bg-purple-50 rounded-lg">
                  <div className="text-lg font-bold text-purple-600">
                    {Math.round(((group.user_count || 0) / (group.max_users || 100)) * 100)}%
                  </div>
                  <div className="text-xs text-gray-500">使用率</div>
                </div>
              </div>

              {/* 容量进度条 */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>容量使用</span>
                  <span>{group.user_count || 0} / {group.max_users || 100}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all"
                    style={{ 
                      width: `${Math.min(((group.user_count || 0) / (group.max_users || 100)) * 100, 100)}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      
      {/* 覆盖导入模态框 */}
      {showOverrideModal && selectedGroup && (
        <OverrideImportModal
          group={selectedGroup}
          onClose={() => {
            setShowOverrideModal(false)
            setSelectedGroup(null)
          }}
          onSuccess={() => {
            setShowOverrideModal(false)
            setSelectedGroup(null)
            queryClient.invalidateQueries({ queryKey: ['edgetunnel-nodes'] })
            queryClient.invalidateQueries({ queryKey: ['edgetunnel-groups'] })
          }}
        />
      )}
    </div>
  )
}

// 节点标签页组件
function NodesTab({ nodes, groups }: any) {
  const [selectedNodes, setSelectedNodes] = useState<string[]>([])
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingNode, setEditingNode] = useState<any>(null)
  const [showBatchActions, setShowBatchActions] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [selectedGroupId, setSelectedGroupId] = useState<string>('all')
  const [searchKeyword, setSearchKeyword] = useState('')
  const queryClient = useQueryClient()

  // 删除节点
  const deleteNodeMutation = useMutation({
    mutationFn: (id: string) => edgeTunnelApi.deleteNode(id),
    onSuccess: () => {
      toast.success('节点删除成功')
      queryClient.invalidateQueries({ queryKey: ['edgetunnel-nodes'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '删除失败')
    },
  })

  // 批量删除节点
  const batchDeleteMutation = useMutation({
    mutationFn: (data: any) => edgeTunnelApi.batchDeleteNodes(data),
    onSuccess: () => {
      toast.success('批量删除成功')
      setSelectedNodes([])
      queryClient.invalidateQueries({ queryKey: ['edgetunnel-nodes'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '批量删除失败')
    },
  })

  // 批量更新节点
  const batchUpdateMutation = useMutation({
    mutationFn: (data: any) => edgeTunnelApi.batchUpdateNodes(data),
    onSuccess: () => {
      toast.success('批量更新成功')
      setSelectedNodes([])
      queryClient.invalidateQueries({ queryKey: ['edgetunnel-nodes'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '批量更新失败')
    },
  })

  const getUserLevelText = (level: number) => {
    switch (level) {
      case 1: return '所有用户'
      case 2: return '标准用户'
      case 3: return 'VIP专享'
      default: return '未知'
    }
  }

  const getUserLevelColor = (level: number) => {
    switch (level) {
      case 1: return 'bg-green-100 text-green-800'
      case 2: return 'bg-blue-100 text-blue-800'
      case 3: return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const handleSelectNode = (nodeId: string) => {
    setSelectedNodes(prev => 
      prev.includes(nodeId) 
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    )
  }

  const handleSelectAll = () => {
    if (selectedNodes.length === currentNodes.length) {
      setSelectedNodes([])
    } else {
      setSelectedNodes(currentNodes.map((node: any) => node.id.toString()))
    }
  }

  const handleDeleteNode = (node: any) => {
    if (confirm(`确定要删除节点 ${node.ip_address} 吗？`)) {
      deleteNodeMutation.mutate(node.id.toString())
    }
  }

  const handleEditNode = (node: any) => {
    setEditingNode(node)
    setShowEditModal(true)
  }

  const handleBatchDelete = () => {
    if (selectedNodes.length === 0) {
      toast.error('请选择要删除的节点')
      return
    }
    
    if (confirm(`确定要删除选中的 ${selectedNodes.length} 个节点吗？`)) {
      batchDeleteMutation.mutate({ node_ids: selectedNodes })
    }
  }

  const handleBatchUpdateStatus = (status: number) => {
    if (selectedNodes.length === 0) {
      toast.error('请选择要更新的节点')
      return
    }
    
    batchUpdateMutation.mutate({ 
      node_ids: selectedNodes, 
      status 
    })
  }

  // 筛选逻辑
  const filteredNodes = nodes.filter((node: any) => {
    // 服务组筛选
    if (selectedGroupId !== 'all' && node.group_id.toString() !== selectedGroupId) {
      return false
    }
    
    // 关键词搜索
    if (searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase()
      return (
        node.ip_address.toLowerCase().includes(keyword) ||
        (node.name && node.name.toLowerCase().includes(keyword)) ||
        (node.location && node.location.toLowerCase().includes(keyword))
      )
    }
    
    return true
  })

  // 分页逻辑
  const totalNodes = filteredNodes.length
  const totalPages = Math.ceil(totalNodes / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const currentNodes = filteredNodes.slice(startIndex, endIndex)

  // 重置页码当筛选条件改变时
  const resetPage = () => {
    setCurrentPage(1)
    setSelectedNodes([])
  }

  // 当筛选条件改变时重置页码
  React.useEffect(() => {
    resetPage()
  }, [selectedGroupId, searchKeyword])

  if (nodes.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">暂无EdgeTunnel节点</h3>
          <p className="text-gray-500 mb-4">创建服务组时会自动添加节点</p>
        </CardContent>
      </Card>
    )
  }

  if (filteredNodes.length === 0) {
    return (
      <div className="space-y-4">
        {/* 筛选和搜索工具栏 */}
        <div className="bg-white p-4 rounded-lg border space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            {/* 服务组筛选 */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">服务组:</label>
              <select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">全部服务组 ({nodes.length})</option>
                {groups.map((group: any) => {
                  const groupNodeCount = nodes.filter((node: any) => node.group_id === group.id).length
                  return (
                    <option key={group.id} value={group.id.toString()}>
                      {group.name} ({groupNodeCount})
                    </option>
                  )
                })}
              </select>
            </div>

            {/* 搜索框 */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">搜索:</label>
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                placeholder="搜索IP地址、节点名称或位置..."
                className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
              />
            </div>

            {/* 清除筛选 */}
            {(selectedGroupId !== 'all' || searchKeyword.trim()) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedGroupId('all')
                  setSearchKeyword('')
                }}
              >
                清除筛选
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardContent className="text-center py-12">
            <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">无匹配的节点</h3>
            <p className="text-gray-500 mb-4">
              {selectedGroupId !== 'all' ? '该服务组下没有节点' : '没有找到匹配的节点'}
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSelectedGroupId('all')
                setSearchKeyword('')
              }}
            >
              清除筛选条件
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* 筛选和搜索工具栏 */}
      <div className="bg-white p-4 rounded-lg border space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          {/* 服务组筛选 */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">服务组:</label>
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">全部服务组 ({nodes.length})</option>
              {groups.map((group: any) => {
                const groupNodeCount = nodes.filter((node: any) => node.group_id === group.id).length
                return (
                  <option key={group.id} value={group.id.toString()}>
                    {group.name} ({groupNodeCount})
                  </option>
                )
              })}
            </select>
          </div>

          {/* 搜索框 */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">搜索:</label>
            <input
              type="text"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder="搜索IP地址、节点名称或位置..."
              className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
          </div>

          {/* 清除筛选 */}
          {(selectedGroupId !== 'all' || searchKeyword.trim()) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedGroupId('all')
                setSearchKeyword('')
              }}
            >
              清除筛选
            </Button>
          )}
        </div>

        {/* 显示筛选结果统计 */}
        <div className="text-sm text-gray-500">
          {selectedGroupId !== 'all' && (
            <span>
              服务组: {groups.find((g: any) => g.id.toString() === selectedGroupId)?.name} • 
            </span>
          )}
          {searchKeyword.trim() && (
            <span>
              搜索: "{searchKeyword}" • 
            </span>
          )}
          <span>
            找到 {totalNodes} 个节点
            {totalNodes !== nodes.length && ` (共 ${nodes.length} 个)`}
          </span>
        </div>
      </div>

      {/* 批量操作工具栏 */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg border">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selectedNodes.length === currentNodes.length && currentNodes.length > 0}
              onChange={handleSelectAll}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-600">
              全选当前页 ({selectedNodes.length}/{currentNodes.length})
            </span>
          </label>
          
          {selectedNodes.length > 0 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBatchUpdateStatus(1)}
                className="text-green-600 hover:text-green-700"
              >
                批量启用
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBatchUpdateStatus(0)}
                className="text-orange-600 hover:text-orange-700"
              >
                批量禁用
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBatchDelete}
                className="text-red-600 hover:text-red-700"
              >
                批量删除
              </Button>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            {totalNodes > 0 ? (
              <>显示 {startIndex + 1}-{Math.min(endIndex, totalNodes)} / 共 {totalNodes} 个节点</>
            ) : (
              <>无匹配节点</>
            )}
          </div>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value))
              setCurrentPage(1)
            }}
            className="text-sm border border-gray-300 rounded px-2 py-1"
          >
            <option value={20}>每页 20 个</option>
            <option value={50}>每页 50 个</option>
            <option value={100}>每页 100 个</option>
            <option value={200}>每页 200 个</option>
          </select>
        </div>
      </div>

      {/* 节点列表 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentNodes.map((node: any) => {
          const group = groups.find((g: any) => g.id === node.group_id)
          const isSelected = selectedNodes.includes(node.id.toString())
          
          return (
            <Card key={node.id} className={`hover:shadow-md transition-shadow ${isSelected ? 'ring-2 ring-blue-500' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelectNode(node.id.toString())}
                      className="rounded border-gray-300"
                    />
                    <Globe className="h-4 w-4 text-blue-500" />
                    <span className="font-medium">{node.name || node.ip_address}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant={node.status === 1 ? "default" : "secondary"}>
                      {node.status === 1 ? '启用' : '禁用'}
                    </Badge>
                    <div className="flex items-center gap-1 ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditNode(node)}
                        title="编辑节点"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteNode(node)}
                        title="删除节点"
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">服务组:</span>
                    <span>{group?.name || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">IP地址:</span>
                    <span className="font-mono">{node.ip_address}</span>
                  </div>
                  {node.location && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">位置:</span>
                      <span>{node.location}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">用户等级:</span>
                    <Badge className={getUserLevelColor(node.user_level)}>
                      {getUserLevelText(node.user_level)}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">连接数:</span>
                    <span>{node.current_connections || 0} / {node.max_connections || 100}</span>
                  </div>
                </div>

                {/* 使用率进度条 */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>使用率</span>
                    <span>{Math.round(((node.current_connections || 0) / (node.max_connections || 100)) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-blue-500 h-1.5 rounded-full transition-all"
                      style={{ 
                        width: `${Math.min(((node.current_connections || 0) / (node.max_connections || 100)) * 100, 100)}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* 分页控件 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white p-4 rounded-lg border">
          <div className="text-sm text-gray-500">
            第 {currentPage} 页，共 {totalPages} 页
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              上一页
            </Button>
            
            {/* 页码按钮 */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className="w-8 h-8 p-0"
                  >
                    {pageNum}
                  </Button>
                )
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              下一页
            </Button>
          </div>
        </div>
      )}

      {/* 节点编辑模态框 */}
      {showEditModal && editingNode && (
        <NodeEditModal
          node={editingNode}
          groups={groups}
          onClose={() => {
            setShowEditModal(false)
            setEditingNode(null)
          }}
          onSuccess={() => {
            setShowEditModal(false)
            setEditingNode(null)
            queryClient.invalidateQueries({ queryKey: ['edgetunnel-nodes'] })
          }}
        />
      )}
    </div>
  )
}

// 用户分配标签页组件
function AssignmentsTab({ assignments }: any) {
  console.log('AssignmentsTab received assignments:', assignments);
  console.log('Assignments length:', assignments?.length);
  console.log('Assignments type:', typeof assignments);
  
  if (!assignments || assignments.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <UserPlus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">暂无用户分配</h3>
          <p className="text-gray-500 mb-4">用户购买套餐后会自动分配节点</p>
          <div className="text-xs text-gray-400 mt-2">
            调试信息: assignments={JSON.stringify(assignments)}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                用户
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                节点
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                UUID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                分配时间
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                到期时间
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                状态
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {assignments.map((assignment: any) => (
              <tr key={assignment.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Users className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {assignment.username || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {assignment.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{assignment.node_name || assignment.node_location}</div>
                  <div className="text-sm text-gray-500">{assignment.group_name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                    {assignment.uuid}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(assignment.assigned_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {assignment.expires_at ? new Date(assignment.expires_at).toLocaleDateString() : '永久'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Badge variant={assignment.is_active ? "default" : "secondary"}>
                    {assignment.is_active ? '活跃' : '已过期'}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}